import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavContainer, MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectComponent } from '@ng-select/ng-select';
import { FilterComponent } from 'app/common/components/filter/filter.component';
import { MonedaPipe } from 'app/pipes/moneda.pipe';
import { FormatOtherDateHourPipe } from 'app/pipes/other-date.pipe';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { RoutesFiltersComponent } from '../../filters/routes-filters/routes-filters.component';
import { getCode } from 'app/utils/method';
import { CustomerRouteFrequencyService } from './customer-route-frequency.service';
import { Subscription } from 'rxjs';
import { subRoutesFrecuencyRequestDto, subRoutesFrecuencyResponseDto } from './dto/sub-route-reports.dto';
import { IPaginationResultFrequencies } from 'app/common/interfaces'
import { SubRouteTypeEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/sub-route-type.enum';
import { ReportsRoutesExport } from '../../assistant/delivery/loads/exports/reports/reports-route.export';
import { CUSTOMER_ROUTE_FREQUENCY_EXPORT_ID, CUSTOMER_ROUTE_FREQUENCY_PROPERTIES, CUSTOMER_ROUTE_FREQUENCY_TITLE } from '../../assistant/delivery/loads/exports/reports/reports-route.constants';
import { ExportDialogComponent, ExportOptions } from 'app/layout/export-overlay';
import { MatDialog } from '@angular/material/dialog';
import { CustomMatPaginatorIntl } from 'app/mock-api/common/customPaginatorIntl';
import { UserRouteDisplayComponent } from 'app/shared/components/user-route-display/user-route-display.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SearchService } from 'app/layout/common/search-page/search-page.service';
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { MatMenuModule } from "@angular/material/menu";
import { SmartGridComponent, ColDef, GridConfig, PageChangedEvent, SortChangedEvent } from 'app/shared/components/smart-grid';
import { RouteUserCellRendererComponent } from './cell-renderers/route-user-cell-renderer.component';

const daysMap: { [key: number]: string } = {
    1: 'L',
    2: 'MA',
    3: 'MI',
    4: 'J',
    5: 'V',
    6: 'S',
    7: 'D',
  };

@Component({
  selector: "app-customer-route-frequency",
  standalone: true,
  imports: [
    CommonModule,
    MatSidenavContainer,
    MatButtonModule,
    MatIconModule,
    SmartGridComponent,
    MatSidenavModule,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    FormatOtherDateHourPipe,
    MonedaPipe,
    NgSelectComponent,
    MatTooltipModule,
    MatBadgeModule,
    FilterComponent,
    RoutesFiltersComponent,
    UserRouteDisplayComponent,
    HoverMenuDirective,
    MatMenuModule,
  ],
  templateUrl: "./customer-route-frequency.component.html",
  styleUrl: "./customer-route-frequency.component.scss",
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    ReportsRoutesExport,
  ],
})
export class CustomerRouteFrequencyComponent implements OnInit, OnDestroy {
  getCode = getCode;

  data: subRoutesFrecuencyResponseDto[] = [];
  columnDefs: ColDef<subRoutesFrecuencyResponseDto>[] = [];
  gridConfig: GridConfig<any> = {
    rowIdField: "_id" as any,
    emptyState: {
      message: "No encontramos tus resultados!",
      showIcon: true,
    },
    pagination: {
      enabled: true,
      mode: "api",
      pageSize: 50,
      pageSizeOptions: [50, 100, 150, 200],
      showPaginator: true,
    },
    sorting: {
      enabled: true,
      mode: "api",
      multiColumn: false,
      defaultSort: [{ field: "total" as any, direction: "desc" }],
    },
    search: {
      enabled: false,
    },
    columnFilters: {
      enabled: true,
      mode: "local",
    },
    columnVisibility: {
      enabled: false, // Disabled - now in settings panel
    },
    selection: {
      enabled: false,
    },
    autoSizing: {
      enabled: true,
      includeHeaders: true,
      skipHidden: true,
      padding: 40, // Increased for more spacing
    },
    styling: {
      maxHeight: "85vh",
      minHeight: "80vh",
      density: "comfortable",
      fontSize: "12px",
      cellPaddingX: "3px",
      cellPaddingY: "3px",
    },
    settingsPanel: {
      enabled: true,
      position: "right",
      enableColumnVisibility: true,
      defaultDensity: "compact",
    },
  };

  totalItems: number = 0;
  search: string = "";
  pageSize: number = 50;
  pageIndex: number = 1;
  isLoading: boolean = false;

  form: FormGroup;
  searchSubscription: Subscription;

  loadFiltersData = false;

  constructor(
    private fb: FormBuilder,
    private customerRouteFrequencyService: CustomerRouteFrequencyService,
    private reportsRouteExport: ReportsRoutesExport,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private readonly searchService: SearchService
  ) {
    this.form = this.fb.group({
      routeId: [],
      subRouteId: [],
      routeTypeId: [],
      functionCodes: [],
    });

    this.initializeColumnDefs();
  }

  private initializeColumnDefs(): void {
    this.columnDefs = [
      {
        field: "routeName" as any,
        headerName: "Territorio",
        minWidth: 200,
        sortable: true,
        valueFormatter: (value, data) => String(value || "-"),
        filterOptions: {
          valueExtractor: (item: any) => String(item.routeName || "-"),
          customFilterFn: (rowData: any, filterValue: string) => {
            const value = String(rowData.routeName || "-");
            if (!value || value === "-") return !filterValue;
            return value === filterValue;
          },
        },
      },
      {
        field: "subRouteName" as any,
        headerName: "Ruta - Responsable",
        minWidth: 250,
        sortable: false,
        cellRenderer: RouteUserCellRendererComponent,
        filterOptions: {
          valueExtractor: (item: any) => {
            const routeName = item.subRouteName ?? "-";
            const userName = item.userName ?? "-";
            return `${routeName} - ${userName}`;
          },
          customFilterFn: (rowData: any, filterValue: string) => {
            const routeName = rowData.subRouteName ?? "-";
            const userName = rowData.userName ?? "-";
            const displayValue = `${routeName} - ${userName}`;
            if (!displayValue || displayValue === "- - -") return !filterValue;
            return displayValue === filterValue;
          },
        },
      },
      {
        field: "type",
        headerName: "Tipo",
        minWidth: 100,
        sortable: true,
        valueFormatter: (value, data) => String(value || "-"),
        filterOptions: {
          valueExtractor: (item: any) => String(item.type || "-"),
          customFilterFn: (rowData: any, filterValue: string) => {
            const value = String(rowData.type || "-");
            if (!value || value === "-") return !filterValue;
            return value === filterValue;
          },
        },
      },
      {
        field: "function",
        headerName: "Función",
        minWidth: 150,
        sortable: true,
        valueFormatter: (value, data) => String(value || "-"),
        filterOptions: {
          valueExtractor: (item: any) => String(item.function || "-"),
          customFilterFn: (rowData: any, filterValue: string) => {
            const value = String(rowData.function || "-");
            if (!value || value === "-") return !filterValue;
            return value === filterValue;
          },
        },
      },
      {
        field: "custmerCode" as any,
        headerName: "Código cliente",
        minWidth: 150,
        sortable: true,
        valueFormatter: (value, data) => String(value || "-"),
        filterOptions: {
          valueExtractor: (item: any) => String(item.custmerCode || "-"),
          customFilterFn: (rowData: any, filterValue: string) => {
            const value = String(rowData.custmerCode || "-");
            if (!value || value === "-") return !filterValue;
            return value === filterValue;
          },
        },
      },
      {
        field: "custmerName" as any,
        headerName: "Nombre cliente",
        minWidth: 250,
        sortable: true,
        valueFormatter: (value, data) => String(value || "-"),
        filterOptions: {
          valueExtractor: (item: any) => String(item.custmerName || "-"),
          customFilterFn: (rowData: any, filterValue: string) => {
            const value = String(rowData.custmerName || "-");
            if (!value || value === "-") return !filterValue;
            return value === filterValue;
          },
        },
      },
      {
        field: "days",
        headerName: "Días",
        minWidth: 120,
        sortable: true,
        valueFormatter: (value, data) => String(value || "-"),
        filterOptions: {
          valueExtractor: (item: any) => String(item.days || "-"),
          customFilterFn: (rowData: any, filterValue: string) => {
            const value = String(rowData.days || "-");
            if (!value || value === "-") return !filterValue;
            return value === filterValue;
          },
        },
      },
    ];
  }

  onFilterPanelOpened(): void {
    this.loadFiltersData = true;
  }

  ngOnInit(): void {
    this.onHandleSearch();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.findAllWithCount();
    }, 0);
  }

  public onHandleSearch() {
    this.searchSubscription = this.searchService.searchTerm$.subscribe(
      (term) => {
        this.pageIndex = 1;
        this.search = term;
        this.findAllWithCount();
      }
    );
  }

  onExportButtonPressed() {
    if (
      !this.reportsRouteExport.canStartTask(CUSTOMER_ROUTE_FREQUENCY_EXPORT_ID)
    )
      return;

    const dialogRef = this.dialog.open(ExportDialogComponent, {
      data: { title: CUSTOMER_ROUTE_FREQUENCY_TITLE },
    });

    let dataToFilter: subRoutesFrecuencyRequestDto = {
      subRouteIds: this.form.value.subRouteId ? this.form.value.subRouteId : [],
      type: this.form.value.routeTypeId,
      routesIds: this.form.value.routeId ? this.form.value.routeId : [],
      functionCodes: this.form.value.functionCodes
        ? this.form.value.functionCodes
        : [],
      search: this.search,
      page: this.pageIndex,
      limit: this.pageSize,
    };

    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        // this.chargePartnerExport.setData(this.dataSourceChargePartners.data);
        this.reportsRouteExport.exportCustomerRouteFrequency(
          options.format,
          options.range,
          CUSTOMER_ROUTE_FREQUENCY_EXPORT_ID,
          CUSTOMER_ROUTE_FREQUENCY_TITLE,
          this.totalItems,
          dataToFilter,
          CUSTOMER_ROUTE_FREQUENCY_PROPERTIES
        );
      }
    });
  }

  onSortChange(event: SortChangedEvent<subRoutesFrecuencyResponseDto>): void {
    this.pageIndex = 1;
    this.findAllWithCount();
  }

  onClearFilter() {
    this.form.reset();
    this.findAllWithCount();
  }

  onPageChange(event: PageChangedEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex + 1;
    this.findAllWithCount();
  }

  findAllWithCount(): void {
    let dataToFilter: subRoutesFrecuencyRequestDto = {
      subRouteIds: this.form.value.subRouteId ? this.form.value.subRouteId : [],
      type: this.form.value.routeTypeId,
      routesIds: this.form.value.routeId ? this.form.value.routeId : [],
      functionCodes: this.form.value.functionCodes
        ? this.form.value.functionCodes
        : [],
      search: this.search,
      page: this.pageIndex,
      limit: this.pageSize,
    };

    this.isLoading = true;
    this.customerRouteFrequencyService
      .findAllWithCount(dataToFilter)
      .subscribe({
        next: (
          res: IPaginationResultFrequencies<subRoutesFrecuencyResponseDto>
        ) => {
          const transformedData = (res?.data?.data ?? []).map((item) => ({
            ...item,
            type:
              item.type === SubRouteTypeEnum.URBAN
                ? "URBANA"
                : item.type === SubRouteTypeEnum.FOREIGN
                  ? "FORÁNEA"
                  : item.type,
            days: (item.frecuencyDays ?? [])
              .sort((a, b) => a - b)
              .map((day) => daysMap[day])
              .filter(Boolean)
              .join(","),
          }));

          this.data = transformedData ?? [];
          this.totalItems = res?.data?.pageInfo?.[0]?.total ?? 0;
          this.isLoading = false;
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error al obtener información del reporte.",
            "Cerrar",
            { duration: 5000 }
          );
          this.isLoading = false;
        },
      });
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }
}
